import React, { useState } from 'react';
import { Button } from 'primereact/button';
import styled from 'styled-components';
import { Content, Parallax } from '../../App';
import Footer from '../../components/Footer';
import { deliveryLocations, DeliveryLocationDetail } from '../../shared/utils/deliveryLocation.utils';

// Styled components for polished, responsive landing page layout
const HeroOverlay = styled.div`
  background: linear-gradient(90deg, rgba(16, 28, 12, 0.88) 0%, rgba(16, 28, 12, 0.72) 55%, rgba(16, 28, 12, 0.40) 100%);
`;

const SectionBadge = styled.div<{ bg?: string; color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.875rem;
  border-radius: 9999px;
  background-color: ${props => props.bg || 'var(--green-20)'};
  color: ${props => props.color || 'var(--green)'};
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
`;

const StepCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8ede4;
  border-radius: 1rem;
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(116, 143, 86, 0.12);
  }
`;

const StepNumber = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background: var(--orange-20);
  color: var(--orange);
  font-weight: 700;
  font-size: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;

const InteractivePickerCard = styled.div`
  background: #ffffff;
  border: 2px solid var(--green-40);
  border-radius: 1.25rem;
  box-shadow: 0 10px 30px rgba(116, 143, 86, 0.12);
  padding: 1.5rem;

  @media (min-width: 768px) {
    padding: 2.25rem;
  }
`;

const OptionButton = styled.button<{ active: boolean }>`
  background: ${props => (props.active ? 'var(--green-20)' : '#ffffff')};
  border: 2px solid ${props => (props.active ? 'var(--green)' : '#e2e8f0')};
  color: ${props => (props.active ? 'var(--green)' : '#334155')};
  border-radius: 0.875rem;
  padding: 1rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.18s ease;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  &:hover {
    border-color: var(--green);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--green-40);
  }
`;

const FormulaCard = styled.button<{ active: boolean }>`
  background: ${props => (props.active ? 'linear-gradient(135deg, rgba(116, 143, 86, 0.12) 0%, rgba(238, 122, 49, 0.08) 100%)' : '#ffffff')};
  border: 2px solid ${props => (props.active ? 'var(--orange)' : '#e2e8f0')};
  border-radius: 0.875rem;
  padding: 1rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.18s ease;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:hover {
    border-color: var(--orange);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--orange-20);
  }
`;

const SummaryBox = styled.div`
  background: linear-gradient(135deg, rgba(116, 143, 86, 0.15) 0%, rgba(238, 122, 49, 0.12) 100%);
  border-radius: 1rem;
  padding: 1.25rem;
  border: 1px solid rgba(116, 143, 86, 0.25);
`;

const FaqItem = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  margin-bottom: 0.75rem;
  overflow: hidden;
  transition: border-color 0.2s ease;

  &[data-open='true'] {
    border-color: var(--green);
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  padding: 1.125rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  background: none;
  border: none;
  text-align: left;
  font-size: 1.05rem;
  font-weight: 600;
  color: #1e293b;
  cursor: pointer;

  &:hover {
    color: var(--green);
  }

  &:focus {
    outline: none;
    background: rgba(116, 143, 86, 0.05);
  }
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1.25rem 1.25rem;
  color: #475569;
  line-height: 1.6;
  font-size: 0.975rem;
`;

const LocationAccordionItem = styled.div<{ isOpen: boolean }>`
  background: #ffffff;
  border: 1px solid ${props => (props.isOpen ? 'var(--green)' : '#e2e8f0')};
  border-radius: 0.75rem;
  overflow: hidden;
  transition: all 0.2s ease;
`;

const LocationHeader = styled.button`
  width: 100%;
  padding: 0.875rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  gap: 0.5rem;

  &:hover {
    background: rgba(116, 143, 86, 0.04);
  }

  &:focus {
    outline: none;
    background: rgba(116, 143, 86, 0.08);
  }
`;

// Data structures
type SizeKey = 'small' | 'large';
type FormulaKey = 'flex40' | 'flex30' | 'flex15' | 'trial8';

interface SizeConfig {
  id: SizeKey;
  name: string;
  tagline: string;
  portions: string;
  vegetables: string;
}

interface FormulaConfig {
  id: FormulaKey;
  name: string;
  packagesCount: number;
  rhythm: string;
  description: string;
  scheduleNote?: string;
  prices: Record<SizeKey, {
    total: number;
    code: string;
  }>;
}

const SIZES: SizeConfig[] = [
  {
    id: 'small',
    name: 'Klein pakket',
    tagline: 'Ideaal voor 1 tot 2 personen',
    portions: '± 3 à 4 maaltijden per week',
    vegetables: '5 tot 7 verse seizoensgroenten',
  },
  {
    id: 'large',
    name: 'Groot pakket',
    tagline: 'Voor gezinnen of echte groenteliefhebbers',
    portions: '± 4 à 5 royale gezinsmaaltijden',
    vegetables: '7 tot 9 verse seizoensgroenten',
  },
];

const FORMULAS: FormulaConfig[] = [
  {
    id: 'flex40',
    name: 'Flex 40',
    packagesCount: 40,
    rhythm: 'Wekelijks',
    description: 'Het hele oogstseizoen genieten van kraakverse biogroenten.',
    scheduleNote: 'Wekelijks (feb - mrt tweewekelijks, jan geen pakketten)',
    prices: {
      small: {
        total: 755,
        code: 'pakketten-2023-flex-40-medium',
      },
      large: {
        total: 1090,
        code: 'pakketten-2023-flex-40-large',
      },
    },
  },
  {
    id: 'flex30',
    name: 'Flex 30',
    packagesCount: 30,
    rhythm: 'Wekelijks met vakantieruimte',
    description: 'Wekelijks ritme met flexibiliteit om verlofperiodes over te slaan.',
    scheduleNote: '30 pakketten te verdelen over het oogstseizoen (jan geen pakketten)',
    prices: {
      small: {
        total: 600,
        code: 'pakketten-2023-flex-30-medium',
      },
      large: {
        total: 860,
        code: 'pakketten-2023-flex-30-large',
      },
    },
  },
  {
    id: 'flex15',
    name: 'Flex 15',
    packagesCount: 15,
    rhythm: 'Tweewekelijks',
    description: 'Om de week een pakket, ideaal naast eigen tuin of vaste winkels.',
    scheduleNote: 'Tweewekelijks doorheen het jaar (jan geen pakketten)',
    prices: {
      small: {
        total: 310,
        code: 'pakketten-2023-flex-15-medium',
      },
      large: {
        total: 440,
        code: 'pakketten-2023-flex-15-large',
      },
    },
  },
  {
    id: 'trial8',
    name: 'Proef 8',
    packagesCount: 8,
    rhythm: 'Proefperiode',
    description: 'Ervaar 8 pakketten lang de smaak, versheid en eenvoud.',
    scheduleNote: '8 opeenvolgende pakketten zonder verdere verplichting',
    prices: {
      small: {
        total: 160,
        code: 'pakketten-2023-flex-trial-medium',
      },
      large: {
        total: 230,
        code: 'pakketten-2023-flex-trial-large',
      },
    },
  },
];

const FAQS = [
  {
    question: 'Hoe kan ik mijn pakket aanpassen?',
    answer:
      'Wekelijks stellen wij een gebalanceerd seizoenspakket samen. Via onze handige webapp kan je dit naar wens finetunen: wissel groenten om, verhoog porties of voeg lokale hoeveproducten toe (zoals biologische eieren of aardappelen). Doe je niets? Dan ontvang je automatisch het voorstel van de boer.',
  },
  {
    question: 'Kan ik een pakket verplaatsen bij vakantie of drukte?',
    answer:
      'Natuurlijk! Ga je op vakantie of komt een pakket even niet goed uit? Via onze webapp verschuif je het eenvoudig naar een andere week. Afhankelijk van je formule kan dat tot 5 keer. Zo past je groentepakket zich aan jouw agenda aan en niet omgekeerd!',
  },
  {
    question: 'Hoe en wanneer betaal ik?',
    answer:
      'Je schrijft je veilig in via ons MijnBoer-platform. ' +
      'De betaling verloopt via overschrijving of in termijnen volgens de abonnementsvoorwaarden. Er zijn geen verborgen kosten.',
  },
  {
    question: 'Hoe werkt de proefperiode (Proef 8)?',
    answer:
      'Met Proef 8 ontvang je 8 pakketten. ' +
      'Dit is de ideale manier om zonder langdurig engagement kennis te maken met onze biologische groenten, ' +
      'de flexibele webapp en het afhaalpunt in jouw buurt.',
  },
  {
    question: 'Wanneer kan ik starten?',
    answer:
      'Onze groentepakketten staan elke vrijdag vers voor je klaar op het gekozen afhaalpunt. ' +
      'Tijdens je online inschrijving via MijnBoer ziet je meteen jouw startdatum.',
  },
];

const formatCurrency = (amount: number): string => {
  return amount.toLocaleString('nl-BE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const BasketsPageNew: React.FC = () => {
  const [selectedSize, setSelectedSize] = useState<SizeKey>('small');
  const [selectedFormula, setSelectedFormula] = useState<FormulaKey>('flex40');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [openLocationIndex, setOpenLocationIndex] = useState<number | null>(null);

  const currentFormula = FORMULAS.find(f => f.id === selectedFormula) || FORMULAS[0];
  const currentSize = SIZES.find(s => s.id === selectedSize) || SIZES[0];
  const pricing = currentFormula.prices[selectedSize];
  const avgPricePerPackage = pricing.total / currentFormula.packagesCount;

  const currentSignupUrl = `https://www.mijnboer.be/groentegeweld/inschrijven/pakketten?code=${pricing.code}`;

  const scrollToPicker = () => {
    const el = document.getElementById('pakketkiezer');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(prev => (prev === index ? null : index));
  };

  const toggleLocation = (index: number) => {
    setOpenLocationIndex(prev => (prev === index ? null : index));
  };

  return (
    <div>
      {/* 1. HERO SECTIE */}
      <section className="relative overflow-hidden">
        <Parallax img={'IMG_7906.jpg'} height={'62vh'} />
        <HeroOverlay className="absolute top-0 left-0 right-0 bottom-0 flex align-items-center">
          <Content>
            <div className="py-5 md:py-7 text-white" style={{ maxWidth: '720px' }}>
              <SectionBadge bg="rgba(255, 255, 255, 0.22)" color="#ffffff">
                <i className="pi pi-check-circle" />
                <span>100% biologisch &amp; lokaal geteeld</span>
              </SectionBadge>

              <h1 className="m-0 mb-3 text-3xl md:text-5xl lg:text-6xl line-height-1 font-bold text-white">
                Verse biogroenten. Zonder boodschappenstress.
              </h1>

              <p className="text-base md:text-xl line-height-3 mb-4 text-gray-100">
                Elke week of om de week een verrassend pakket vol smaak, rechtstreeks van ons veld. Wij doen een voorstel, jij kiest wat je graag eet.
              </p>

              <div className="flex flex-wrap gap-2 md:gap-3 mb-4 text-sm font-medium">
                <span className="px-3 py-1 border-round-3xl" style={{ background: 'rgba(255, 255, 255, 0.2)' }}>
                  ✓ Flexibel aanpasbaar
                </span>
                <span className="px-3 py-1 border-round-3xl" style={{ background: 'rgba(255, 255, 255, 0.2)' }}>
                  ✓ Afhalen op vrijdag
                </span>
                <span className="px-3 py-1 border-round-3xl" style={{ background: 'rgba(255, 255, 255, 0.2)' }}>
                  ✓ Korte keten
                </span>
              </div>

              {/* Strategische CTA 1: Scroll naar pakketkiezer */}
              <Button
                label="Ontdek jouw pakket"
                icon="pi pi-arrow-down"
                iconPos="right"
                className="color-white bg-orange p-button-lg border-none shadow-3"
                onClick={scrollToPicker}
              />
            </div>
          </Content>
        </HeroOverlay>
      </section>

      {/* 2. HOE WERKT HET */}
      <Content>
        <section className="py-5 md:py-6">
          <div className="text-center mb-5">
            <SectionBadge>
              <i className="pi pi-sync" />
              <span>Eenvoudig in 3 stappen</span>
            </SectionBadge>
            <h2 className="mt-1 mb-2 text-2xl md:text-4xl color-green font-bold">
              Hoe werkt het?
            </h2>
            <p className="m-0 text-base md:text-lg text-gray-700 line-height-3 mx-auto" style={{ maxWidth: '640px' }}>
              Gezond en vers eten zonder keuzestress. Wij oogsten, jij geniet.
            </p>
          </div>

          <div className="grid">
            <div className="col-12 md:col-4">
              <StepCard>
                <StepNumber>1</StepNumber>
                <h3 className="text-xl font-bold color-green mb-2">Kies je pakket</h3>
                <p className="text-gray-600 line-height-3 m-0 flex-grow-1">
                  Selecteer het formaat dat past bij jouw gezin en bepaal hoe vaak je groenten wilt ontvangen.
                </p>
              </StepCard>
            </div>

            <div className="col-12 md:col-4">
              <StepCard>
                <StepNumber>2</StepNumber>
                <h3 className="text-xl font-bold color-green mb-2">Pas aan als je wilt</h3>
                <p className="text-gray-600 line-height-3 m-0 flex-grow-1">
                  Elke week stellen wij je pakket samen. Helemaal goed zo? Dan hoef je niets te doen. Liever iets aanpassen of een weekje overslaan? Dat kan ook!
                </p>
              </StepCard>
            </div>

            <div className="col-12 md:col-4">
              <StepCard>
                <StepNumber>3</StepNumber>
                <h3 className="text-xl font-bold color-green mb-2">Haal vers af</h3>
                <p className="text-gray-600 line-height-3 m-0 flex-grow-1">
                  Elke vrijdag staat jouw kraakverse oogstpakket klaar bij het afhaalpunt van jouw keuze in de buurt.
                </p>
              </StepCard>
            </div>
          </div>
        </section>
      </Content>

      {/* 3. INTERACTIEVE PAKKETKIEZER */}
      <Content>
        <section id="pakketkiezer" className="py-4 md:py-6">
          <div className="text-center mb-4 md:mb-5">
            <h2 className="mt-1 mb-2 text-2xl md:text-4xl color-green font-bold">
              Stel jouw groentepakket samen
            </h2>
            <p className="m-0 text-base md:text-lg text-gray-700 line-height-3 mx-auto" style={{ maxWidth: '640px' }}>
              Kies jouw formaat en gewenste ritme.
            </p>
          </div>

          <InteractivePickerCard>
            {/* STAP A: PAKKETGROOTTE */}
            <div className="mb-4 md:mb-5">
              <div className="flex align-items-center gap-2 mb-3">
                <span className="font-bold text-lg color-green">Stap 1:</span>
                <span className="font-semibold text-gray-800 text-base">Kies je pakketgrootte</span>
              </div>
              <div className="grid">
                {SIZES.map(size => {
                  const isActive = selectedSize === size.id;
                  return (
                    <div key={size.id} className="col-12 sm:col-6">
                      <OptionButton
                        type="button"
                        active={isActive}
                        onClick={() => setSelectedSize(size.id)}
                        aria-pressed={isActive}
                      >
                        <div className="flex justify-content-between align-items-center w-full">
                          <span className="font-bold text-lg color-green">{size.name}</span>
                          <i className={`pi ${isActive ? 'pi-check-circle color-green' : 'pi-circle text-300'} text-xl`} />
                        </div>
                        <div className="font-medium text-sm color-orange">{size.tagline}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          {size.portions} • {size.vegetables}
                        </div>
                      </OptionButton>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* STAP B: FORMULE */}
            <div className="mb-4 md:mb-5">
              <div className="flex align-items-center gap-2 mb-3">
                <span className="font-bold text-lg color-green">Stap 2:</span>
                <span className="font-semibold text-gray-800 text-base">Kies jouw formule</span>
              </div>
              <div className="grid">
                {FORMULAS.map(formula => {
                  const isActive = selectedFormula === formula.id;
                  const price = formula.prices[selectedSize];
                  const avg = price.total / formula.packagesCount;
                  return (
                    <div key={formula.id} className="col-12 sm:col-6 lg:col-3">
                      <FormulaCard
                        type="button"
                        active={isActive}
                        onClick={() => setSelectedFormula(formula.id)}
                        aria-pressed={isActive}
                      >
                        <div>
                          <div className="flex justify-content-between align-items-start mb-2">
                            <span className="font-bold text-lg color-green">{formula.name}</span>
                            <span className="text-xs px-2 py-1 border-round font-semibold bg-gray-100 text-gray-700">
                              {formula.packagesCount} pakketten
                            </span>
                          </div>
                          <div className="text-xs font-semibold color-orange mb-2">
                            {formula.rhythm}
                          </div>
                          <div className="text-xs text-gray-600 line-height-2 mb-3">
                            {formula.description}
                          </div>
                        </div>

                        <div className="pt-2 border-top-1 border-200">
                          <div className="text-xs text-gray-500">Gemiddeld per pakket</div>
                          <div className="text-xl font-bold color-orange">
                            € {formatCurrency(avg)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Totaalbedrag: € {price.total}
                          </div>
                        </div>
                      </FormulaCard>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* SAMENVATTING EN DUIDELIJKE CTA */}
            <SummaryBox>
              <div className="grid align-items-center">
                <div className="col-12 md:col-7">
                  <div className="text-xs uppercase tracking-wide font-bold color-green mb-1">
                    Jouw selectie
                  </div>
                  <h3 className="m-0 text-xl md:text-2xl font-bold color-green mb-1">
                    {currentSize.name} — {currentFormula.name} ({currentFormula.packagesCount} pakketten)
                  </h3>
                  <p className="text-sm text-gray-700 line-height-2 m-0 mb-2">
                    {currentFormula.scheduleNote || currentFormula.description}
                  </p>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                    <span>✓ Inhoud flexibel aanpasbaar</span>
                    <span>✓ Uitstellen tijdens vakantie mogelijk</span>
                    <span>✓ Afhaling op vrijdag</span>
                  </div>
                </div>

                <div className="col-12 md:col-5 text-left md:text-right">
                  <div className="mb-3">
                    <span className="text-xs text-gray-600 block">Gemiddeld per pakket</span>
                    <span className="text-3xl font-bold color-orange">
                      € {formatCurrency(avgPricePerPackage)}
                    </span>
                    <span className="text-xs text-gray-500 block">
                      Totaal: € {pricing.total} ({currentFormula.packagesCount} leveringen)
                    </span>
                  </div>

                  {/* Strategische CTA 2: Direct naar MijnBoer inschrijving met geselecteerde optie */}
                  <Button
                    label={`Kies ${currentFormula.name} (${currentSize.name === 'Klein pakket' ? 'Klein' : 'Groot'})`}
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    className="w-full md:w-auto color-white bg-orange p-button-lg border-none shadow-2"
                    onClick={() => window.open(currentSignupUrl, '_blank')}
                  />
                  <div className="text-xs text-gray-500 mt-2">
                    Veilig inschrijven via MijnBoer
                  </div>
                </div>
              </div>
            </SummaryBox>
          </InteractivePickerCard>
        </section>
      </Content>

      {/* 4. AFHAALPUNTEN */}
      <Content>
        <section className="py-4 md:py-6">
          <div className="text-center mb-4">
            <SectionBadge>
              <i className="pi pi-map-marker" />
              <span>Elke vrijdag vers</span>
            </SectionBadge>
            <h2 className="mt-1 mb-2 text-2xl md:text-4xl color-green font-bold">
              Afhaalpunten in jouw buurt
            </h2>
            <p className="m-0 text-base md:text-lg text-gray-700 line-height-3 mx-auto" style={{ maxWidth: '640px' }}>
              Jouw biopakket staat elke <strong>vrijdag</strong> klaar bij één van onze lokale verdeelpunten. Klik op een gemeente voor adres en openingsuren.
            </p>
          </div>

          <div className="grid">
            {deliveryLocations.map((location: DeliveryLocationDetail, index: number) => {
              const isOpen = openLocationIndex === index;
              return (
                <div key={location.value} className="col-12 sm:col-6 lg:col-4">
                  <LocationAccordionItem isOpen={isOpen}>
                    <LocationHeader
                      type="button"
                      onClick={() => toggleLocation(index)}
                      aria-expanded={isOpen}
                    >
                      <div className="flex align-items-center gap-2 overflow-hidden">
                        <i className="pi pi-map-marker color-orange text-lg flex-shrink-0" />
                        <span className="font-bold text-base text-gray-800 white-space-nowrap text-overflow-ellipsis">
                          {location.city}
                        </span>
                        {location.onlyTrial && (
                          <span className="text-xs px-2 py-0 border-round bg-yellow-100 text-yellow-800 font-medium">
                            Proef
                          </span>
                        )}
                      </div>
                      <i className={`pi ${isOpen ? 'pi-chevron-up' : 'pi-chevron-down'} text-gray-500 text-sm`} />
                    </LocationHeader>

                    {isOpen && (
                      <div className="px-3 pb-3 pt-1 border-top-1 border-100 text-sm text-gray-600 line-height-3 bg-gray-50">
                        <div className="font-semibold text-gray-800">{location.name}</div>
                        <div>{location.address}, {location.city}</div>
                        <div className="mt-2 text-xs font-medium color-green">
                          <i className="pi pi-clock mr-1" />
                          Vrijdag {location.shopHours?.[0] || 'in de namiddag'}
                        </div>
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${location.address}, ${location.city}`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex align-items-center gap-1 color-orange font-semibold text-xs mt-2 no-underline"
                        >
                          <span>Bekijk op kaart</span>
                          <i className="pi pi-external-link text-xs" />
                        </a>
                      </div>
                    )}
                  </LocationAccordionItem>
                </div>
              );
            })}
          </div>
        </section>
      </Content>

      {/* 5. VERTROUWEN EN FAQ */}
      <Content>
        <section className="py-4 md:py-6">
          <div className="grid align-items-center mb-5">
            <div className="col-12 md:col-5">
              <div className="border-round-2xl overflow-hidden shadow-2">
                <img
                  src="formule-pakket2.jpg"
                  alt="Verse biologische groenten van Groentegeweld"
                  className="w-full block"
                  style={{ maxHeight: '340px', objectFit: 'cover' }}
                />
              </div>
            </div>
            <div className="col-12 md:col-7 md:pl-4">
              <SectionBadge bg="var(--orange-20)" color="var(--orange)">
                <i className="pi pi-heart" />
                <span>Recht van het veld</span>
              </SectionBadge>
              <h2 className="mt-1 mb-3 text-2xl md:text-3xl color-green font-bold">
                Eerlijke groenten, rechtstreeks van de bioboer
              </h2>
              <p className="text-gray-700 line-height-3 text-base m-0 mb-3">
                Bij Groentegeweld telen we met respect voor bodem en natuur. Geen lange transporten of anonieme supermarktketens, maar verse seizoensgroenten die geoogst worden wanneer de smaak optimaal is.
              </p>
              <p className="text-gray-700 line-height-3 text-base m-0">
                Zo geniet jij van puur eten en geef je lokale boeren zekerheid.
              </p>
            </div>
          </div>

          <div className="mt-5">
            <div className="text-center mb-4">
              <SectionBadge>
                <i className="pi pi-question-circle" />
                <span>Veelgestelde vragen</span>
              </SectionBadge>
              <h2 className="mt-1 mb-2 text-2xl md:text-3xl color-green font-bold">
                Alles wat je wilt weten over onze pakketten
              </h2>
            </div>

            <div className="mx-auto" style={{ maxWidth: '780px' }}>
              {FAQS.map((faq, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <FaqItem key={faq.question} data-open={isOpen}>
                    <FaqQuestion
                      type="button"
                      onClick={() => toggleFaq(index)}
                      aria-expanded={isOpen}
                    >
                      <span>{faq.question}</span>
                      <i className={`pi ${isOpen ? 'pi-chevron-up color-green' : 'pi-chevron-down text-gray-500'}`} />
                    </FaqQuestion>
                    {isOpen && <FaqAnswer>{faq.answer}</FaqAnswer>}
                  </FaqItem>
                );
              })}
            </div>
          </div>

          {/* AFSLUITENDE CARD MET STRATEGISCHE CTA 3 */}
          <div className="mt-5 md:mt-6">
            <div
              className="border-round-2xl shadow-2 p-4 md:p-6 text-center"
              style={{
                background:
                  'linear-gradient(135deg, rgba(116, 143, 86, 0.16) 0%, rgba(238, 122, 49, 0.12) 100%)',
              }}
            >
              <SectionBadge bg="#ffffff" color="var(--green)">
                <i className="pi pi-check" />
                <span>Klaar voor gezonde biogroenten?</span>
              </SectionBadge>

              <h2 className="mt-2 mb-2 text-2xl md:text-4xl color-green font-bold">
                Schrijf je vandaag nog in
              </h2>

              <p className="text-base md:text-lg text-gray-700 line-height-3 mx-auto mb-4" style={{ maxWidth: '600px' }}>
                Kies jouw formule en afhaalpunt via MijnBoer en geniet binnenkort van kraakverse groenten recht van het veld.
              </p>

              {/* Strategische CTA 3 */}
              <Button
                label="Schrijf je in voor een groentepakket"
                icon="pi pi-external-link"
                iconPos="right"
                className="color-white bg-orange p-button-lg border-none shadow-3"
                onClick={() => window.open(currentSignupUrl, '_blank')}
              />

              <div className="mt-3 text-sm text-gray-600">
                Vragen voor je start? Mail ons gerust via{' '}
                <a className="color-orange font-bold no-underline" href="mailto:info@groentegeweld.be">
                  info@groentegeweld.be
                </a>
              </div>
            </div>
          </div>
        </section>
      </Content>

      <Footer />
    </div>
  );
};

export default BasketsPageNew;
